// Owner-only audit log viewer, styled after iOS Settings / Mail:
// large left-aligned title, pill search field with a Filters disclosure,
// generously-padded list rows, and a card-grouped detail view. Below
// the md breakpoint the list and detail become a two-screen stack with
// an iOS-style "‹ Audit" back button.
//
// The server enforces audit.view; the client guard here is cosmetic —
// if the server returns 401/403 we render a soft "Not available" state
// rather than redirecting, because the dev harness mounts this at
// #/audit which stays visible even to signed-out users.

import { useEffect, useMemo, useState } from "react";
import {
  type AuditEvent,
  type AuditFilters,
  listAuditEvents,
  readSessionToken,
} from "../data/audit.ts";
import { AuditList, type LoadState } from "./AuditList.tsx";
import { DetailView } from "./DetailView.tsx";
import { FilterBar } from "./FilterBar.tsx";

export function AuditLog() {
  const token = readSessionToken();
  const [filters, setFilters] = useState<AuditFilters>({});
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState({ kind: "unauthorized" });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    listAuditEvents(token, filters).then((r) => {
      if (cancelled) return;
      if (r.ok) {
        setState({ kind: "loaded", events: r.events, nextCursor: r.nextCursor });
      } else if (r.status === 401 || r.status === 403) {
        setState({ kind: "unauthorized" });
      } else {
        setState({ kind: "error", status: r.status, error: r.error });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token, filters]);

  const selected = useMemo<AuditEvent | null>(() => {
    if (state.kind !== "loaded" || !selectedId) return null;
    return state.events.find((e) => e.id === selectedId) ?? null;
  }, [state, selectedId]);

  const showDetail = selected != null;

  return (
    <main className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <div className="md:flex md:min-h-screen md:items-stretch">
        <section
          className={`${showDetail ? "hidden md:block" : "block"} md:flex-1 md:border-r md:border-slate-200`}
        >
          <div className="px-5 pt-10 pb-4 md:px-8 md:pt-10">
            <h1 className="text-[32px] md:text-[34px] leading-tight font-bold tracking-tight">
              Audit log
            </h1>
          </div>
          <FilterBar filters={filters} onChange={setFilters} />
          <AuditList
            state={state}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onLoadMore={() => {
              if (state.kind !== "loaded" || !state.nextCursor) return;
              setFilters((f) => ({ ...f, after: state.nextCursor! }));
            }}
          />
        </section>

        <section
          className={`${showDetail ? "block" : "hidden md:block"} md:w-[460px] lg:w-[520px] bg-slate-50`}
        >
          {selected ? (
            <DetailView event={selected} onBack={() => setSelectedId(null)} />
          ) : (
            <div className="h-full grid place-items-center p-10">
              <p className="text-sm text-slate-400">Select an event to see details.</p>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
